import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import CleanButton from "../../common/components/CleanButton";
import { formattedDate } from "../../common/constants/Helpers";
import { Tooltips } from "../../common/constants/Tooltips";
import { LookupRequest } from "../../models/lookup-request";
import { buildLookupRequest, buildSummaryRequest } from "../../state/connect-api";
import { useGetCaseSummaryMutation, useLookupCaseMutation } from "../../state/globalApi";
import { resetCaseMaster, setActiveCaseNumber, setQueryResult, updateCaseMaster } from "../../state/globalStore";

const useActiveWarrants = () => {
  const [condition, setCondition] = useState(null);
  const [search, setSearch] = useState(null);
  const [data, setData] = useState([]);
  const [visibility, setVisibility] = useState<boolean>(false);
  const history = useNavigate();
  const dispatch = useDispatch();
  const [lookupCase, { isLoading }] = useLookupCaseMutation();
  const [getCaseSummary, result] = useGetCaseSummaryMutation();
  const loginType = localStorage.getItem("LOGINTYPE");

  const handleSearch = async () => {
    const lookupRequest: LookupRequest = buildLookupRequest("WAR", "TRUE");
    const results = await lookupCase(lookupRequest).unwrap();
    dispatch(setQueryResult({ LIST: results?.LIST }));
    setData(results?.LIST);
  };

  const handleSelectCase = async (caseNumber: string) => {
    dispatch(setActiveCaseNumber(caseNumber));
    const request: LookupRequest = buildSummaryRequest(caseNumber);
    const results = await getCaseSummary(request).unwrap();
    dispatch(updateCaseMaster({ summary: results }));
    history("/warrant/detail", { replace: true, state: { fromPage: "AW" } });
  };

  useEffect(() => {
    dispatch(resetCaseMaster());
    dispatch(setActiveCaseNumber(null));
  }, [dispatch]);

  const columns = [
    { key: "case", dataIndex: "CASE", title: "Case" },
    { key: "name", dataIndex: "NAME", title: "Name" },
    { key: "dob", dataIndex: "DATE_OF_BIRTH", title: "DOB" },
    { key: "offense", dataIndex: "OFFENSE", title: "Offense" },
    { key: "date", dataIndex: "VIOLATION_DATE", title: "Date", render: (date) => formattedDate(date) },
    { key: "status", dataIndex: "STATDEF", title: "Status" },
    { key: "actions", dataIndex: "CASE", title: "Actions", render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectCase(caseNumber)} /> }
  ];

  const fixedColumns = [
    { key: "field", dataIndex: "FIELD", title: "Field" },
    { key: "description", dataIndex: "DESCRIPTION", title: "Description" }
  ];

  const fixedData = [
    { FIELD: "Name", DESCRIPTION: "Last name,First name or Last name only. Comma between names required. Space after comma optional. (Example: Smith,Jonathan or Smith)" },
    {
      FIELD: "Soundex",
      DESCRIPTION:
        "Sound Index Search (How it sounds, rather than spelled) Last name (or Last name,First name - comma required). (Example: Jacksen or Smyth,James) NOTE! These searches can take an extended period of time to complete!"
    },
    { FIELD: "Case", DESCRIPTION: "Case Number. Format - 2 Digit Year, Case Type, Number (Example: 12TRC12345)." },
    { FIELD: "DOB", DESCRIPTION: "Date of Birth in the format MM/DD/YYYY (Example: 05/07/1968)." },
    { FIELD: "Ticket", DESCRIPTION: "Traffic Ticket number." },
    { FIELD: "License Plate", DESCRIPTION: "Vehicle License Plate Number." },
    { FIELD: "Incident", DESCRIPTION: "Incident Number." },
    { FIELD: loginType === "A" ? "SSN" : null, DESCRIPTION: loginType === "A" ? "Social Security Number in the format NNN-NN-NNNN. (Example: 123-45-6789)." : null },
    { FIELD: loginType === "A" ? "FID/ALT ID" : null, DESCRIPTION: loginType === "A" ? "Federal or Alternate ID." : null }
  ];

  const handleViewPDF = () => {
    setVisibility(true);
  };

  return {
    data,
    condition,
    setCondition,
    search,
    setSearch,
    handleSearch,
    columns,
    loading: isLoading,
    setData,
    fixedColumns,
    fixedData,
    visibility,
    setVisibility,
    handleViewPDF
  };
};

export default useActiveWarrants;
